// Underlined tabs for detail pages (store History / Upcoming / Info / Files, like RecordDetailTabs). WAI-ARIA tabs
// pattern: one tab stop, arrows move between tabs and activate them, Home/End jump. When `children` is a function the
// component also renders the active panel with the right ids; otherwise it is just the tab list.

import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './cn';

export interface TabItem<V extends string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  /** Small count badge after the label. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<V extends string> {
  value: V;
  onValueChange: (value: V) => void;
  items: readonly TabItem<V>[];
  /** Accessible name of the tab list. */
  label: string;
  /** Renders the panel for the active tab. */
  children?: (active: V) => ReactNode;
  className?: string;
  listClassName?: string;
  panelClassName?: string;
}

export function Tabs<V extends string>({
  value,
  onValueChange,
  items,
  label,
  children,
  className,
  listClassName,
  panelClassName,
}: TabsProps<V>) {
  const baseId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = items.map((t, i) => (t.disabled ? -1 : i)).filter((i) => i >= 0);
  const tabId = (v: V) => `tab${baseId}-${v}`;
  const panelId = (v: V) => `panel${baseId}-${v}`;

  const select = (index: number) => {
    const item = items[index];
    if (!item) return;
    onValueChange(item.value);
    refs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (enabled.length === 0) return;
    const at = enabled.indexOf(index);
    let next: number | undefined;
    if (event.key === 'ArrowRight') next = enabled[(at + 1) % enabled.length];
    else if (event.key === 'ArrowLeft') next = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'End') next = enabled[enabled.length - 1];
    if (next === undefined) return;
    event.preventDefault();
    select(next);
  };

  const activeIndex = items.findIndex((t) => t.value === value);

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={label}
        className={cn('flex items-end gap-1 overflow-x-auto border-b border-line gt-scroll', listClassName)}
      >
        {items.map((item, index) => {
          const selected = index === activeIndex;
          return (
            <button
              key={item.value}
              ref={(node) => {
                refs.current[index] = node;
              }}
              id={tabId(item.value)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={children ? panelId(item.value) : undefined}
              tabIndex={selected || (activeIndex < 0 && index === enabled[0]) ? 0 : -1}
              disabled={item.disabled}
              onClick={() => onValueChange(item.value)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={cn(
                'relative -mb-px inline-flex h-10 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-semibold',
                'transition-colors disabled:opacity-40 [&_svg]:size-4',
                selected
                  ? 'border-primary text-fg'
                  : 'border-transparent text-fg-muted hover:border-line-strong hover:text-fg',
              )}
            >
              {item.icon}
              {item.label}
              {item.count !== undefined && (
                <span
                  className={cn(
                    'min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 font-bold tabular-nums',
                    selected ? 'bg-primary-soft text-primary-text' : 'bg-surface-3 text-fg-muted',
                  )}
                >
                  {item.count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {children && activeIndex >= 0 && (
        <div
          role="tabpanel"
          id={panelId(value)}
          aria-labelledby={tabId(value)}
          tabIndex={0}
          className={cn('pt-4 focus-visible:outline-offset-4', panelClassName)}
        >
          {children(value)}
        </div>
      )}
    </div>
  );
}
